import { DrawerContentWidthVariant } from './Drawer.types'

export const DRAWER_DESKTOP_LEFT_OFFSET = 16
export const DRAWER_SIDEBAR_WIDTH = 50
export const DRAWER_DEFAULT_CONTENT_WIDTH = 360
export const DRAWER_EXPANDED_CONTENT_MAX_WIDTH = 660
export const DRAWER_EXPANDED_CONTENT_VIEWPORT_OFFSET = 64

export function getDesktopDrawerContentWidth(variant: DrawerContentWidthVariant, viewportWidth?: number): number {
  if (variant === 'adhp-expanded') {
    const resolvedViewportWidth =
      viewportWidth ??
      (typeof window !== 'undefined'
        ? window.innerWidth
        : DRAWER_EXPANDED_CONTENT_MAX_WIDTH + DRAWER_EXPANDED_CONTENT_VIEWPORT_OFFSET)

    return Math.min(
      DRAWER_EXPANDED_CONTENT_MAX_WIDTH,
      Math.max(0, resolvedViewportWidth - DRAWER_EXPANDED_CONTENT_VIEWPORT_OFFSET)
    )
  }

  return DRAWER_DEFAULT_CONTENT_WIDTH
}

/**
 * Escalonamento da barra lateral do Drawer por altura de viewport.
 *
 * A sidebar empilha ~15 botões e, no tamanho padrão (40px + 8px de gap), não cabe mais em
 * viewports abaixo de ~735px de altura — medido com Playwright varrendo de 1000px a 380px.
 * Cada degrau é o ponto (com folga) em que o tamanho anterior deixa de caber.
 *
 * A contagem de botões varia com feature flags, então os degraus são uma aproximação:
 * `SideBarContent` ganha rolagem abaixo do último degrau como rede de segurança.
 *
 * A largura da sidebar acompanha o botão (`optionSize` + 4px de cada lado), e a pílula de beta
 * mantém a proporção do tamanho padrão: a largura do botão, metade da altura dele e a fonte na
 * mesma razão. `betaPillBottom` deixa a pílula 1px antes do botão seguinte, como os -7px do padrão
 * fazem com o gap de 8px.
 */
export const DRAWER_SIDEBAR_SIZE_STEPS = [
  { maxHeight: 745, optionSize: 36, iconSize: 22, gap: 6, betaPillHeight: 18, betaPillFontSize: 9, betaPillBottom: -5 },
  { maxHeight: 665, optionSize: 32, iconSize: 20, gap: 4, betaPillHeight: 16, betaPillFontSize: 8, betaPillBottom: -3 },
  { maxHeight: 585, optionSize: 28, iconSize: 18, gap: 4, betaPillHeight: 14, betaPillFontSize: 7, betaPillBottom: -3 },
  { maxHeight: 515, optionSize: 24, iconSize: 16, gap: 4, betaPillHeight: 12, betaPillFontSize: 6, betaPillBottom: -3 }
] as const

/** Abaixo desta altura nem o menor degrau cabe: a sidebar passa a rolar. */
export const DRAWER_SIDEBAR_SCROLL_MAX_HEIGHT = 450

/**
 * Escalonamento da lista de texto do Drawer expandido (`MainDrawer`), nas mesmas alturas dos
 * degraus da sidebar. Os 14 itens de 40px (+8px de margem) mais o logo pedem ~720px: abaixo de
 * ~747px de viewport os últimos itens ("Novidades", "Configurações") ficavam cortados, sem rolagem.
 * Cada degrau foi dimensionado para caber até o degrau seguinte.
 */
export const DRAWER_MAIN_ITEM_SIZE_STEPS = [
  { maxHeight: 745, itemHeight: 36, itemMargin: 2, fontSize: 15 },
  { maxHeight: 665, itemHeight: 32, itemMargin: 2, fontSize: 14 },
  { maxHeight: 585, itemHeight: 28, itemMargin: 1, fontSize: 13 },
  { maxHeight: 515, itemHeight: 24, itemMargin: 1, fontSize: 12 }
] as const
